import { useEffect, useRef, useState } from "react";
import { Camera, Image as ImageIcon, X, Check, Sparkle, ArrowClockwise, Trash, Plus, VideoCamera, Warning } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const LOCATIONS = [
    { v: "fridge", l: "Frigo" },
    { v: "freezer", l: "Congélateur" },
    { v: "pantry", l: "Placard" },
];

const FRESHNESS = {
    fresh: { l: "Frais", cls: "bg-fzgreen text-white" },
    check: { l: "À vérifier", cls: "bg-fzyellow text-zinc-900" },
    spoiled: { l: "Douteux", cls: "bg-fzred text-white" },
};

async function resizeImage(file, maxSize = 800, quality = 0.75) {
    // Robust decoding: try createImageBitmap first, fallback to HTMLImageElement
    let width, height, source;
    try {
        const bitmap = await createImageBitmap(file);
        width = bitmap.width; height = bitmap.height; source = bitmap;
    } catch (_) {
        const img = new Image();
        const url = URL.createObjectURL(file);
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
        width = img.naturalWidth; height = img.naturalHeight; source = img;
        URL.revokeObjectURL(url);
    }
    const scale = Math.min(1, maxSize / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(source, 0, 0, w, h);
    // Use FileReader.readAsDataURL — safe for images of any size (String.fromCharCode(...) crashes >65KB)
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) throw new Error("Impossible d'encoder l'image");
    return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result); // already 'data:image/jpeg;base64,...'
        reader.onerror = () => reject(new Error("Lecture image échouée"));
        reader.readAsDataURL(blob);
    });
}

export default function ScanModal({ onClose, onAdded }) {
    const [images, setImages] = useState([]); // {url, id}
    const [location, setLocation] = useState("fridge");
    const [scanning, setScanning] = useState(false);
    const [detected, setDetected] = useState(null); // {items, summary}
    const [err, setErr] = useState("");
    const [cameraOn, setCameraOn] = useState(false);
    const [facingMode, setFacingMode] = useState("environment");
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const fileRef = useRef(null);
    const nativeCamRef = useRef(null);

    // Handle Escape
    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") close(); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
        // eslint-disable-next-line
    }, []);

    const close = () => {
        stopCamera();
        onClose();
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        setCameraOn(false);
    };

    const startCamera = async (mode = facingMode) => {
        setErr("");
        stopCamera();
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setErr("Votre navigateur ne supporte pas l'accès à la caméra. Utilisez Safari (iOS) ou Chrome (Android/Desktop) sur HTTPS.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: mode }, width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: false,
            });
            streamRef.current = stream;
            setFacingMode(mode);
            setCameraOn(true); // triggers render of <video>; useEffect will attach the stream
        } catch (e) {
            const name = e && e.name ? e.name : "Erreur";
            let msg;
            if (name === "NotAllowedError" || name === "PermissionDeniedError") {
                msg = "Permission caméra refusée. Autorisez l'accès dans les paramètres du navigateur (icône cadenas dans la barre d'adresse).";
            } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
                msg = "Aucune caméra détectée sur cet appareil.";
            } else if (name === "NotReadableError" || name === "TrackStartError") {
                msg = "Caméra déjà utilisée par une autre application. Fermez-la et réessayez.";
            } else {
                msg = "Impossible d'accéder à la caméra : " + (e.message || name);
            }
            setErr(msg);
            setCameraOn(false);
        }
    };

    // Attach stream to <video> once it's mounted
    useEffect(() => {
        if (cameraOn && streamRef.current && videoRef.current) {
            const v = videoRef.current;
            v.srcObject = streamRef.current;
            v.setAttribute("playsinline", "true"); // required for iOS
            v.muted = true;
            const p = v.play();
            if (p && typeof p.catch === "function") {
                p.catch((err) => setErr("Lecture vidéo bloquée : " + (err.message || err)));
            }
        }
    }, [cameraOn]);

    const flipCamera = async () => {
        await startCamera(facingMode === "environment" ? "user" : "environment");
    };

    const capture = async () => {
        if (!videoRef.current) return;
        const v = videoRef.current;
        const canvas = document.createElement("canvas");
        const maxW = 1024;
        const scale = Math.min(1, maxW / v.videoWidth);
        canvas.width = v.videoWidth * scale;
        canvas.height = v.videoHeight * scale;
        canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
        const url = canvas.toDataURL("image/jpeg", 0.85);
        setImages((prev) => [...prev, { id: Math.random().toString(36).slice(2), url }].slice(0, 6));
    };

    const handleFiles = async (fileList) => {
        setErr("");
        const arr = Array.from(fileList).slice(0, 6 - images.length);
        const out = [];
        let failed = 0;
        for (const f of arr) {
            if (!f.type.startsWith("image/")) {
                failed++;
                continue;
            }
            try {
                const url = await resizeImage(f);
                if (url && url.startsWith("data:image/")) {
                    out.push({ id: Math.random().toString(36).slice(2), url });
                } else {
                    failed++;
                }
            } catch (e) {
                console.error("resizeImage failed", e);
                failed++;
            }
        }
        setImages((prev) => [...prev, ...out].slice(0, 6));
        if (failed > 0 && out.length === 0) {
            setErr(`Impossible de charger ${failed} photo(s). Formats acceptés : JPEG, PNG, WEBP.`);
        } else if (failed > 0) {
            setErr(`${failed} photo(s) ignorée(s) (format non supporté).`);
        }
    };

    const removeImg = (id) => setImages(images.filter((i) => i.id !== id));

    const analyze = async () => {
        if (images.length === 0) { setErr("Ajoute au moins 1 photo"); return; }
        // 1) Stop the camera immediately — user no longer needs it, saves battery/bandwidth,
        //    and gives a visible "something is happening" feedback.
        stopCamera();
        // 2) Reset state
        setErr(""); setScanning(true); setDetected(null);
        try {
            const { data } = await api.post("/ai/scan", {
                images: images.map((i) => i.url),
                location,
            }, {
                timeout: 90000, // 90s to accommodate GPT-5.2 vision latency
            });
            const items = (data.items || []).map((it, idx) => ({ ...it, _selected: true, _key: idx }));
            setDetected({ items, summary: data.summary || "" });
            if (items.length === 0) setErr("Aucun aliment reconnu sur ces photos. Essayez avec une image plus nette ou mieux éclairée.");
        } catch (e) {
            // Never lose user's photos on error — they can retry immediately.
            let msg;
            const status = e?.response?.status;
            if (e?.code === "ECONNABORTED" || (e?.message || "").toLowerCase().includes("timeout")) {
                msg = "L'analyse IA a mis trop de temps (>90s). Réessayez avec moins de photos ou une image plus légère.";
            } else if (!e?.response) {
                msg = "Réseau indisponible. Vérifiez votre connexion puis réessayez.";
            } else if (status === 401) {
                msg = "Session expirée. Reconnectez-vous.";
            } else if (status === 500) {
                msg = "Erreur serveur IA (500). Il est probable que la variable EMERGENT_LLM_KEY ne soit pas configurée en production.";
            } else if (status === 503) {
                msg = "Le service IA est temporairement indisponible (503).";
            } else {
                msg = `${formatApiError(e)}${status ? ` (HTTP ${status})` : ""}`;
            }
            setErr(msg);
            console.error("[scan] analyze error:", e);
        } finally {
            setScanning(false);
        }
    };

    const toggle = (idx) => {
        setDetected((d) => ({ ...d, items: d.items.map((it) => it._key === idx ? { ...it, _selected: !it._selected } : it) }));
    };

    const editField = (idx, k, v) => {
        setDetected((d) => ({ ...d, items: d.items.map((it) => it._key === idx ? { ...it, [k]: v } : it) }));
    };

    const confirmAdd = async () => {
        const selected = (detected?.items || []).filter((it) => it._selected).map(({ _selected, _key, ...rest }) => rest);
        if (selected.length === 0) { setErr("Sélectionne au moins un aliment"); return; }
        setErr("");
        try {
            await api.post("/ai/scan/add", { items: selected });
            onAdded && onAdded(selected.length);
            close();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="modal-scan" onClick={close}>
            <div className="bg-white border border-zinc-200 w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-zinc-200">
                    <div className="flex items-center gap-2">
                        <VideoCamera size={20} className="text-fzblue" />
                        <div>
                            <div className="font-display font-bold">Scanner mon frigo</div>
                            <div className="text-xs text-zinc-500">L'IA vision analyse vos photos et détecte les aliments · GPT-5.2</div>
                        </div>
                    </div>
                    <button onClick={close} className="text-zinc-500 hover:text-zinc-900" data-testid="scan-close"><X size={18} /></button>
                </div>

                <div className="p-6 overflow-y-auto relative">
                    {/* Full-panel loading overlay — impossible to miss */}
                    {scanning && (
                        <div className="absolute inset-0 z-10 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center gap-4 p-8" data-testid="scan-loading">
                            <div className="relative">
                                <Sparkle size={48} className="text-fzblue animate-pulse" weight="fill" />
                                <div className="absolute -inset-6 border-2 border-fzblue/30 border-t-fzblue rounded-full animate-spin" />
                            </div>
                            <div className="font-display font-bold text-2xl tracking-tighter text-zinc-950">Analyse IA en cours</div>
                            <div className="text-sm text-zinc-500 text-center max-w-sm">
                                GPT-5.2 examine {images.length} photo{images.length > 1 ? "s" : ""} et identifie vos aliments…<br />
                                <span className="font-mono text-xs">15 à 40 secondes</span>
                            </div>
                        </div>
                    )}
                    {!detected && (
                        <>
                            {/* Camera zone */}
                            <div className="mb-4">
                                {cameraOn ? (
                                    <div className="relative bg-zinc-950 border border-zinc-200 overflow-hidden">
                                        <video ref={videoRef} playsInline muted className="w-full max-h-[420px] object-contain bg-zinc-950" data-testid="scan-video" />
                                        <div className="absolute top-3 left-3 uppercase-tag text-white flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 bg-fzred rounded-full animate-pulse" /> LIVE
                                        </div>
                                        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-3">
                                            <button onClick={capture} data-testid="scan-capture" className="bg-white text-zinc-900 px-5 py-3 font-medium inline-flex items-center gap-2 hover:bg-zinc-100">
                                                <Camera size={16} weight="fill" /> Capturer
                                            </button>
                                            <button onClick={flipCamera} className="bg-white/20 text-white px-3 py-3 hover:bg-white/30" title="Changer caméra">
                                                <ArrowClockwise size={16} />
                                            </button>
                                            <button onClick={stopCamera} className="bg-white/20 text-white px-3 py-3 hover:bg-white/30" title="Éteindre">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid sm:grid-cols-3 gap-3">
                                        <button data-testid="scan-open-camera" onClick={() => startCamera("environment")} className="border border-zinc-300 hover:border-fzblue p-6 flex flex-col items-center gap-2 text-center hover:bg-zinc-50">
                                            <VideoCamera size={28} className="text-fzblue" />
                                            <div className="font-display font-semibold">Caméra en direct</div>
                                            <div className="text-xs text-zinc-500">Multi-capture · Front/arrière</div>
                                        </button>
                                        <button data-testid="scan-native-camera" onClick={() => nativeCamRef.current?.click()} className="border border-zinc-300 hover:border-fzblue p-6 flex flex-col items-center gap-2 text-center hover:bg-zinc-50">
                                            <Camera size={28} className="text-fzblue" weight="fill" />
                                            <div className="font-display font-semibold">Prendre une photo</div>
                                            <div className="text-xs text-zinc-500">Rapide · iOS & Android</div>
                                        </button>
                                        <button data-testid="scan-upload" onClick={() => fileRef.current?.click()} className="border border-zinc-300 hover:border-fzblue p-6 flex flex-col items-center gap-2 text-center hover:bg-zinc-50">
                                            <ImageIcon size={28} className="text-fzblue" />
                                            <div className="font-display font-semibold">Importer des photos</div>
                                            <div className="text-xs text-zinc-500">Jusqu'à 6 images</div>
                                        </button>
                                        <input ref={fileRef} type="file" accept="image/*" multiple hidden
                                            onChange={(e) => handleFiles(e.target.files)} data-testid="scan-file-input" />
                                        <input ref={nativeCamRef} type="file" accept="image/*" capture="environment" hidden
                                            onChange={(e) => handleFiles(e.target.files)} data-testid="scan-native-camera-input" />
                                    </div>
                                )}
                            </div>

                            {/* Thumbnails */}
                            {images.length > 0 && (
                                <div className="mb-4">
                                    <div className="uppercase-tag mb-2">Photos ({images.length}/6)</div>
                                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                                        {images.map((im) => (
                                            <div key={im.id} className="relative border border-zinc-200 group" data-testid={`scan-thumb-${im.id}`}>
                                                <img src={im.url} alt="capture" className="w-full h-24 object-cover" />
                                                <button onClick={() => removeImg(im.id)} className="absolute top-1 right-1 bg-white/90 text-fzred p-1 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Trash size={12} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Location + analyze */}
                            <div className="flex flex-wrap items-end justify-between gap-3">
                                <label className="block">
                                    <div className="uppercase-tag mb-1">Ajouter à</div>
                                    <select data-testid="scan-location" value={location} onChange={(e) => setLocation(e.target.value)}
                                        className="border border-zinc-300 px-3 py-2 bg-white">
                                        {LOCATIONS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
                                    </select>
                                </label>
                                <button data-testid="scan-analyze" onClick={analyze} disabled={images.length === 0 || scanning} className="btn-primary disabled:opacity-40">
                                    <Sparkle size={16} weight="fill" /> {scanning ? "Analyse IA en cours..." : "Analyser avec l'IA"}
                                </button>
                            </div>
                        </>
                    )}

                    {/* Detected result */}
                    {detected && (
                        <div data-testid="scan-results">
                            <div className="mb-4 border-l-2 border-fzblue pl-4">
                                <div className="uppercase-tag">Analyse IA</div>
                                <div className="text-zinc-700 mt-1 text-sm">{detected.summary || "Voici les aliments détectés."}</div>
                            </div>
                            <div className="space-y-2 max-h-[420px] overflow-y-auto">
                                {detected.items.map((it) => {
                                    const f = FRESHNESS[it.freshness] || null;
                                    return (
                                        <div key={it._key} className={"card-flat p-3 flex items-start gap-3 " + (it._selected ? "" : "opacity-50")} data-testid={`scan-item-${it._key}`}>
                                            <input type="checkbox" checked={it._selected} onChange={() => toggle(it._key)} className="mt-1 accent-fzblue" data-testid={`scan-select-${it._key}`} />
                                            <div className="flex-1 grid sm:grid-cols-5 gap-2 items-center">
                                                <input value={it.name} onChange={(e) => editField(it._key, "name", e.target.value)} className="sm:col-span-2 border border-zinc-200 px-2 py-1 text-sm font-medium" />
                                                <div className="flex items-center gap-1">
                                                    <input type="number" step="0.1" value={it.quantity} onChange={(e) => editField(it._key, "quantity", parseFloat(e.target.value) || 0)} className="w-20 border border-zinc-200 px-2 py-1 text-sm" />
                                                    <input value={it.unit} onChange={(e) => editField(it._key, "unit", e.target.value)} className="w-16 border border-zinc-200 px-2 py-1 text-sm" />
                                                </div>
                                                <select value={it.category || "Autre"} onChange={(e) => editField(it._key, "category", e.target.value)} className="border border-zinc-200 px-2 py-1 text-sm bg-white">
                                                    {["Fruits", "Légumes", "Viandes", "Poissons", "Produits laitiers", "Œufs", "Céréales", "Boissons", "Épicerie", "Autre"].map((c) => <option key={c}>{c}</option>)}
                                                </select>
                                                <div className="flex flex-col gap-1">
                                                    {f && <span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${f.cls}`}>{f.l}</span>}
                                                    {it.note && <span className="text-[11px] text-zinc-500 italic truncate" title={it.note}>{it.note}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t border-zinc-200">
                                <button onClick={() => { setDetected(null); }} className="btn-ghost" data-testid="scan-back">
                                    ← Retour
                                </button>
                                <button onClick={confirmAdd} className="btn-primary" data-testid="scan-confirm-add">
                                    <Plus size={16} /> Ajouter au frigo
                                </button>
                            </div>
                        </div>
                    )}

                    {err && (
                        <div className="mt-4 border-2 border-fzred bg-fzred/5 p-4 flex items-start gap-3" data-testid="scan-error" role="alert">
                            <Warning size={20} weight="fill" className="text-fzred shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="font-display font-semibold text-fzred">L'analyse a échoué</div>
                                <div className="text-sm text-zinc-700 mt-1">{err}</div>
                                <div className="text-xs text-zinc-500 mt-2">Vos photos sont conservées — vous pouvez réessayer sans les re-importer.</div>
                            </div>
                            {!detected && images.length > 0 && !scanning && (
                                <button data-testid="scan-retry" onClick={analyze} className="btn-primary shrink-0">
                                    <ArrowClockwise size={16} /> Réessayer
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
